import React, { useRef, useEffect, useState } from 'react';
import { ItemDetail } from '../itemdetail';
import c from 'classnames';
import {
  batchFetchOp,
  selectItemId,
  selectItemIsLoaded,
  selectItemNopreroll,
  selectItemNSFW,
  selectItemsListIsLoaded,
  selectItemsListMediaFilter,
  selectItemStatus,
  selectShowMedia,
  selectShowNsfw,
  selectWideComments,
  selectCommentsUnderVideo,
  selectRelatedListType,
  selectItemMedia,
} from '../../ducks';
import { ListType } from '../../apis/dumpert';
import { getTypeOfListFromCookie } from '../../cookie';
import { Device } from '../../device';
import { ItemsListRelated } from '../itemslist';
import { useSelector, useStore } from 'react-redux';
import { Ads, AdScript, createSlotParams } from '../ads';
import { adBreakPoint, twitchParams } from '../../lib/helpers';
import { Comments } from '../comments';
import { ScrollToTopButton } from '../scrolltotop';
import { useIncreaseItemViews, useCurrentBreakPoint, useItemViewSearchParams } from '../commons';
import { useParams } from 'react-router-dom';

const getListsToFetch = (params, staticContext) => {
  const relatedType = params.listType || getTypeOfListFromCookie('relatedListType', staticContext);
  const lists = [{ type: ListType.related, params }];
  if (relatedType !== ListType.related) {
    lists.push({ type: relatedType, params });
  }
  return lists;
};

export const ItemViewPage = props => {
  const { staticContext } = props;
  const pathParams = useParams();
  const { selectedId } = useItemViewSearchParams();
  const relatedListType = useSelector(selectRelatedListType);
  const itemId = selectedId || pathParams.itemId;
  const params = selectedId ? {
    ...pathParams,
    itemId,
    listType: relatedListType,
  } : pathParams;

  const { dispatch, getState } = useStore();
  const listsToFetch = getListsToFetch(params, staticContext);
  const showMedia = useSelector(selectShowMedia);
  const itemMediaRef = useRef(null);
  const isWideComments = useSelector(selectWideComments);
  const showNsfw = useSelector(selectShowNsfw);
  const itemNSFW = useSelector(selectItemNSFW);
  const status = useSelector(selectItemStatus);
  const nopreroll = useSelector(selectItemNopreroll);
  const itemMedia = useSelector(selectItemMedia);
  const isTwitchLiveEmbed = twitchParams(itemMedia).live;
  const currentBreakPoint = useCurrentBreakPoint();
  const relatedType = listsToFetch[listsToFetch.length - 1].type;
  const [currentItemId, setCurrentItemId] = useState(itemId);
  const [currentShowNsfw, setShowNsfw] = useState(showNsfw);
  const initialCommentUnderVideo = useSelector(selectCommentsUnderVideo);
  const showWideComments = isWideComments || isTwitchLiveEmbed;
  const commentsUnderVideo = isTwitchLiveEmbed ? false : initialCommentUnderVideo;

  useIncreaseItemViews(itemId);

  let showComments = true;

  if (status === 'error') {
    showComments = false;
  }

  useEffect(() => {
    const state = getState();
    const items = selectItemIsLoaded(state) && selectItemId(state) === itemId ? [] : [itemId];
    const nsfwIsUpdated = () => showNsfw !== currentShowNsfw;
    const itemIdIsUpdated = () => itemId !== currentItemId;

    const lists = listsToFetch.filter(

      // required list is not loaded
      x => !selectItemsListIsLoaded(state, x.type)

        // or filter by images/videos has been changed (filter is not applied to related items)
        || (showMedia !== selectItemsListMediaFilter(state, x.type) && x.type !== ListType.related)

        // or nsfwIsUpdated
        || nsfwIsUpdated()
        || itemIdIsUpdated());
    if (items.length > 0 || lists.length > 0 || nsfwIsUpdated() || itemIdIsUpdated()) {

      // if item should be reloaded then related list should be reloaded as well
      if (items.length && !lists.find(x => x.type === ListType.related) || nsfwIsUpdated() || itemIdIsUpdated()) {
        lists.push({ type: ListType.related, params });
      }
      dispatch(batchFetchOp({ items, lists }));
    }
    setShowNsfw(showNsfw);
    setCurrentItemId(itemId);

  }, [itemId, params.listType, showMedia, showNsfw]);

  useEffect(() => {
    if (selectedId) {
      itemMediaRef.current.offsetParent.firstElementChild.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedId]);

  return (
    <AdScript adunit="item" blockads={!!itemNSFW || nopreroll} isModal={!!selectedId}>
      {['XL', 'L', 'M', 'S'].includes(adBreakPoint(currentBreakPoint)) && (
        <Ads {...createSlotParams('leaderboardwallpaper', 't1')} />
      )}
      <div className={c(
        { ['detail-spacing']: selectedId && ['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) },
      )}>
        <div className={c(
          'grid',
          { ['comments-under']: commentsUnderVideo },
        )}>
          <article ref={itemMediaRef} className={c(
            {
              ['full']:
              ['XS', 'S', 'M'].includes(adBreakPoint(currentBreakPoint))
              || ['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) && commentsUnderVideo,
            },
            {
              ['three-fourth']:
              ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
              && !showWideComments
              && !commentsUnderVideo,
            },
            {
              ['two-third']:
              ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
              && showWideComments
              && !commentsUnderVideo,
            },
          )}>
            <ItemDetail 
              isTwitchLiveEmbed={isTwitchLiveEmbed}
              staticContext={staticContext} 
              isModal={!!selectedId} 
            />
            {['XS'].includes(adBreakPoint(currentBreakPoint)) && (
              <Ads {...createSlotParams('mediumrectangle', 't1')} />
            )}
          </article>
          <aside
            className={c(
              { ['full']: ['XS', 'S', 'M'].includes(adBreakPoint(currentBreakPoint)) },
              {
                ['fourth']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
                  && !showWideComments
                  && !commentsUnderVideo,
              },
              {
                ['third']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
                  && showWideComments
                  && !commentsUnderVideo,
              },
              {
                ['half']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
                  && !showWideComments
                  && commentsUnderVideo,
              },
              {
                ['three-fifth']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
                  && showWideComments
                  && commentsUnderVideo,
              },
            )}
            data-type={'comments'}
          >
            {showComments && (
              <div className={c(
                { ['comments-flex']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) },
                { ['twitch']: isTwitchLiveEmbed },
              )}>
                {['XL', 'L'].includes(adBreakPoint(currentBreakPoint)) && !commentsUnderVideo && (
                  <Ads {...createSlotParams('mediumrectangle', 't2', false, false)} />
                )}
                <Comments
                  itemId={itemId}
                  currentBreakPoint={adBreakPoint(currentBreakPoint)}
                  showTwitchChat={isTwitchLiveEmbed}
                />
                {!isTwitchLiveEmbed && (
                  <>
                    {['M', 'S'].includes(adBreakPoint(currentBreakPoint)) &&
                  <Ads {...createSlotParams('leaderboard1', 'b1')} />}
                    {['XS'].includes(adBreakPoint(currentBreakPoint)) &&
                  <Ads {...createSlotParams('rectangleinterscrollerdetail1', 'b1')} />}
                  </>
                )}
              </div>
            )}
          </aside>
          {!isTwitchLiveEmbed && (
            <section
              className={c(
                {
                  ['full']: ['XS', 'S', 'M', 'L', 'XL'].includes(adBreakPoint(currentBreakPoint))
                  && !commentsUnderVideo,
                },
                {
                  ['half']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
                  && !showWideComments
                  && commentsUnderVideo,
                },
                {
                  ['two-fifth']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint))
                  && showWideComments
                  && commentsUnderVideo,
                },
              )}
              data-type="related"
            >
              {['XL', 'L'].includes(adBreakPoint(currentBreakPoint)) && commentsUnderVideo && (
                <Ads {...createSlotParams('mediumrectangle', 't2')} />
              )}            
              <ItemsListRelated
                typeOfList={relatedType}
                itemId={itemId}
                isModal={!!selectedId}
                numOfItems={Device.IS_MOBILE ? 6 : 12}
                hideAds
                isWideComments={showWideComments}
                commentsUnderVideo={commentsUnderVideo}
              />
            </section>
          )}
        </div>
        {!selectedId && <ScrollToTopButton isDetailPage />}
      </div>
      {['XL', 'L'].includes(adBreakPoint(currentBreakPoint)) && (<Ads {...createSlotParams('leaderboard1', 'b1')} />)}
      {['M', 'S'].includes(adBreakPoint(currentBreakPoint)) && (<Ads {...createSlotParams(
        'leaderboard2', showComments ? 'b2' : 'b1')} />)}
      {['XS'].includes(adBreakPoint(currentBreakPoint)) && (<Ads {...createSlotParams(
        'rectangleinterscroller2', showComments ? 'b2' : 'b1')} />)}
    </AdScript>
  );
};

ItemViewPage.serverFetch = ({ params }) => {
  return batchFetchOp({
    items: [params.itemId],

    // do not fetch lists on server to avoid timeout
    // lists: getListsToFetch(params, staticContext),
    lists: [],
  }) ;
};
